import React, { useState } from 'react';
import { Show } from '@refinedev/antd';
import { useShow, useCan, useList } from '@refinedev/core';
import {
  Typography,
  Tag,
  Descriptions,
  Card,
  Button,
  Space,
  List as AntList,
  Divider,
  Spin,
  Result,
  Select,
  message,
} from 'antd';
import { EditOutlined, SafetyOutlined } from '@ant-design/icons';
import { RoleAssignmentEditor, type RoleAssignment } from './components';
import { SuperAdminCompaniesCard } from './components/SuperAdminCompaniesCard';
import { AccountStatusFooter } from './components/AccountStatusFooter';
import type { AuthRole } from './utils';
import { getRoleScopeColor, getRoleScopeLabel } from './utils';
import { authClient } from '../../providers/axios';
import { TEAM_OPTIONS, isTeamKey, teamLabel } from '../../utils/teams';
import { ChangeLogLink } from '../../components/changeLog';

interface UserDetail {
  id: string;
  _id?: string;
  name: string;
  email: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
  roleAssignments?: RoleAssignment[];
  team?: string | null;
  permissions?: string[];
  effectivePermissions?: string[];
}

const getStatusColor = (status: string) => {
  if (status === 'active') return 'green';
  if (status === 'suspended') return 'red';
  return 'default';
};

export const UserDetailPage: React.FC = () => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);

  const { data: canViewUser, isLoading: canViewLoading } = useCan({
    resource: 'users',
    action: 'show',
  });

  const { data: canManageUsers } = useCan({
    resource: 'users',
    action: 'edit',
  });

  const { queryResult, showId } = useShow<UserDetail>({
    resource: 'users',
    meta: { dataProviderName: 'authDataProvider' },
    queryOptions: { enabled: canViewUser?.can === true },
  });

  const user = queryResult?.data?.data;
  const userId = user?.id || user?._id || '';
  // Refine keeps the previous person's record while the next one loads; per-person panels wait
  // for the record of the person in the address bar.
  const isRequestedUser = showId !== undefined && [user?.id, user?._id].includes(String(showId));
  const roleAssignments: RoleAssignment[] = user?.roleAssignments ?? [];
  const permissions: string[] = user?.effectivePermissions ?? user?.permissions ?? [];

  const { data: rolesResult } = useList<AuthRole>({
    resource: 'roles',
    pagination: { mode: 'off' },
    meta: { dataProviderName: 'authDataProvider' },
    queryOptions: { staleTime: 60_000, enabled: canViewUser?.can === true },
  });

  const roleScopeByName = new Map(
    (rolesResult?.data ?? []).map((role) => [role.name, role.scope] as const),
  );

  const handleSaved = () => {
    queryResult.refetch();
  };

  // Clearing the select sends null, which the sign-in service reads as "no team".
  const handleTeamChange = async (team: string | undefined) => {
    setTeamSaving(true);
    try {
      await authClient.patch(`/users/${userId}`, { team: team ?? null });
      message.success('Team updated successfully');
      await queryResult.refetch();
    } catch (err: unknown) {
      // The sign-in service answers errors as { error: { code, message, status } }.
      const detail =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Please try again.';
      message.error(`Failed to update team: ${detail}`);
    } finally {
      setTeamSaving(false);
    }
  };

  if (canViewLoading)
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  if (!canViewUser?.can)
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle="You do not have permission to view this user."
      />
    );

  return (
    <Show
      title={user?.name || 'User Detail'}
      headerButtons={({ defaultButtons }) => (
        <Space>
          {/* Only for the person in the address bar; their roles (sign-in service) and their
              company list (sf-api, by email) in one list. */}
          {isRequestedUser && userId ? (
            <ChangeLogLink
              key={userId}
              subject={{ kind: 'user', authUserId: userId, email: user?.email }}
            />
          ) : null}
          {defaultButtons}
        </Space>
      )}
    >
      {/* Header: core identity */}
      <Descriptions bordered column={1}>
        <Descriptions.Item label="Name">{user?.name || '—'}</Descriptions.Item>
        <Descriptions.Item label="Email">{user?.email || '—'}</Descriptions.Item>
        <Descriptions.Item label="Status">
          {user?.status ? <Tag color={getStatusColor(user.status)}>{user.status}</Tag> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Team">
          {canManageUsers?.can ? (
            <Select
              aria-label="Team"
              placeholder="No team"
              options={TEAM_OPTIONS}
              // null, not undefined: antd treats undefined as uncontrolled, so a failed save
              // would keep showing the team that was not saved.
              value={isTeamKey(user?.team) ? user.team : null}
              onChange={handleTeamChange}
              loading={teamSaving}
              disabled={teamSaving || !userId}
              allowClear
              style={{ width: 240 }}
            />
          ) : (
            (teamLabel(user?.team) ?? 'No team')
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      {/* Metadata */}
      <Descriptions title="Metadata" bordered column={1}>
        <Descriptions.Item label="Created At">
          {user?.createdAt ? new Date(user.createdAt).toLocaleString() : '—'}
        </Descriptions.Item>
        {user?.updatedAt && (
          <Descriptions.Item label="Updated At">
            {new Date(user.updatedAt).toLocaleString()}
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Companies a platform super admin can see in the web app */}
      {isRequestedUser && user?.email && (
        <SuperAdminCompaniesCard
          key={user.email}
          email={user.email}
          canManageUsers={canManageUsers?.can === true}
        />
      )}

      {/* Role Assignments */}
      <Card
        title={
          <Space>
            <SafetyOutlined />
            <span>Role Assignments</span>
          </Space>
        }
        extra={
          canManageUsers?.can ? (
            <Button type="primary" icon={<EditOutlined />} onClick={() => setEditorOpen(true)}>
              Edit Role Assignments
            </Button>
          ) : null
        }
        style={{ marginTop: 16 }}
      >
        {roleAssignments.length > 0 ? (
          <AntList
            dataSource={roleAssignments}
            renderItem={(assignment: RoleAssignment) => (
              <AntList.Item>
                <Space direction="vertical" size={2}>
                  <Space>
                    <Tag>{assignment.role}</Tag>
                    <Tag color={getRoleScopeColor(roleScopeByName.get(assignment.role))}>
                      {getRoleScopeLabel(roleScopeByName.get(assignment.role))}
                    </Tag>
                  </Space>
                  {assignment.customerScope && assignment.customerScope.length > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Customer scope:{' '}
                      {assignment.customerScope.map((c) => (
                        <Tag key={c} color="default" style={{ fontSize: 11 }}>
                          {c}
                        </Tag>
                      ))}
                    </Typography.Text>
                  )}
                </Space>
              </AntList.Item>
            )}
          />
        ) : (
          <Typography.Text type="secondary">No roles assigned</Typography.Text>
        )}
      </Card>

      {/* Effective Permissions */}
      <Card title="Effective Permissions" style={{ marginTop: 16 }}>
        {permissions.length ? (
          <AntList
            dataSource={permissions}
            renderItem={(perm: string) => (
              <AntList.Item key={perm}>
                <Tag color="geekblue">
                  <Typography.Text code>{perm}</Typography.Text>
                </Tag>
              </AntList.Item>
            )}
          />
        ) : (
          <Typography.Text type="secondary">No permissions found</Typography.Text>
        )}
      </Card>

      {/* Waits for the record of the person in the address bar, not the previous one. */}
      {isRequestedUser && user?.email && (
        <AccountStatusFooter key={`account-status:${user.email}`} user={user} />
      )}

      <RoleAssignmentEditor
        userId={userId}
        currentAssignments={roleAssignments}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
      />
    </Show>
  );
};
